import useSnapshotFunctions from 'components/Dashboard/DashboardComponent/useSnapshotFunctions';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useNavigate } from 'react-router-dom';
import { createURLProps } from 'components/Dashboard/DashboardComponent/DashboardUpdateComponent';
import transformToPascalCase from 'components/Settings/Utilities/TransformToPascalCase';
import useLoadContentResults from 'components/Dashboard/ContentControl/useLoadContentResults';

const briefingSectionTitleIconsMapping = {
  drivingtheday: 'calendar',
  incaseyoumissedit: 'double-left-triangle-filled',
  lookingahead: 'double-right-triangle-filled',
  lookingaheadincludingtoday: 'double-right-triangle-filled',
  inthemedia: 'microphone',
};
const briefingSectionEmptyStateMapping = {
  drivingtheday: 'Looks like a quiet day ahead',
  incaseyoumissedit: `We haven't identified any notable updates since your last briefing`,
  lookingahead: `We haven't identified any notable upcoming events for you`,
  lookingaheadincludingtoday: `We haven't identified any notable upcoming events for you`,
  inthemedia: `We haven't identified any notable updates since your last briefing`,
};

const normalizeMarkdownHref = (href) => {
  if (!href) return href;
  const normalizedHref = href.trim();
  // Keep absolute/protocol, hash and already root-relative links unchanged.
  if (
    /^(?:[a-z]+:)?\/\//i.test(normalizedHref) ||
    /^[a-z]+:/i.test(normalizedHref) ||
    normalizedHref.startsWith('/') ||
    normalizedHref.startsWith('#')
  ) {
    return normalizedHref;
  }
  return `/${normalizedHref}`;
};

const BriefingItem = (props) => {
  const {
    item,
    reportItem,
    updateState,
    resetEventParameter,
    noSpacing,
    openLinksInNewTab,
    notShowTitle,
    briefingState,
    restoreScrollPosition,
    notShowThumbsDown,
    noContentFound,
    index,
    array,
  } = props;
  const { body, subject, itemContent } = item ?? {};
  const isMobile = useIsMobile();
  const embargoedCondition = itemContent?.some((item) => item?.embargoed) && !reportItem;
  const itemType = item?.snapshotItemType?.toLowerCase() ?? item?.type?.toLowerCase();
  const emptyCondition = body?.trim() === '' || noContentFound;
  const iconName = briefingSectionTitleIconsMapping[itemType];

  return (
    <div className='px-4 pr-lg-4 pl-lg-2 briefing-item-container'>
      {subject && !notShowTitle && (
        <div
          className={`${noSpacing ? '' : `specific-bottom-margin-10 ${reportItem ? '' : 'bg-main-white pt-4 pb-2'}`}`}
        >
          <h2
            className={`title-h5 font-weight-bold specific-y-padding-12 bg-grey border-general border-grey-50 rounded specific-left-padding-23 briefing-section-title flex-centered justify-content-between flex-wrap ${
              emptyCondition === '' ? 'briefing-empty-title' : ''
            }`}
            style={{ top: '24px' }}
          >
            <span className='flex-centered'>
              {briefingSectionTitleIconsMapping[itemType] && (
                <span
                  className={`icon-${iconName} d-inline-block specific-right-padding-${
                    iconName === 'calendar' ? '15' : '12'
                  } briefing-section-title-icon title-h4`}
                  style={iconName === 'calendar' ? { marginLeft: '2px' } : null}
                />
              )}{' '}
              {subject?.endsWith('.') ? subject.slice(0, -1) : subject}
            </span>
            {embargoedCondition && (
              <div
                className={`d-flex align-items-center pr-1 pr-sm-3 specific-left-margin-${
                  itemType === 'drivingtheday' ? '30' : '35'
                } briefing-item-embargoed`}
              >
                <p className='text-light-red mb-0 specific-right-margin-12'>
                  Note: <span className='font-weight-normal'>References embargoed content</span>
                </p>
                <span
                  className='icon-info text-main-blue-60 pointer'
                  data-tooltip-content={
                    'This briefing references content that was under embargo when the briefing was created. Please be mindful of this if sharing the briefing externally'
                  }
                  data-tooltip-id={'general-tooltip'}
                  data-tooltip-class-name={'general-tooltip pricing-feature-tooltip report-popup-tooltip'}
                />
              </div>
            )}
          </h2>
        </div>
      )}

      <div
        className={`${reportItem || noSpacing ? '' : 'specific-x-padding-22 specific-x-padding-lg-30'} ${
          noSpacing ? '' : `${index === array?.length - 1 ? 'pb-4' : 'pb-2'}`
        }`}
      >
        <div>
          {emptyCondition ? (
            <p
              className={`mb-0 light-60-text title-h5-m ${!reportItem ? 'briefing-item-empty' : ''} ${
                subject && !notShowTitle ? '' : 'pt-5'
              }`}
            >
              {briefingSectionEmptyStateMapping[itemType]}
            </p>
          ) : (
            <>
              <ReactMarkdown
                components={{
                  a: ({ href, children, node, ...props }) => {
                    const normalizedHref = normalizeMarkdownHref(href);
                    if (
                      (!openLinksInNewTab &&
                        normalizedHref &&
                        normalizedHref.startsWith('/') &&
                        !normalizedHref.startsWith('/monitor-inbox/upcoming-events')) ||
                      normalizedHref?.startsWith('https://parliamentary-record/')
                    ) {
                      return (
                        <DashboardUpdateLinkItem
                          href={normalizedHref}
                          restoreScrollPosition={restoreScrollPosition}
                          resetEventParameter={resetEventParameter}
                          updateState={updateState}
                          {...props}
                        >
                          {children}
                        </DashboardUpdateLinkItem>
                      );
                    }
                    // For external links, use <a> with target and rel
                    return !!href ? (
                      <a
                        href={normalizedHref}
                        className='general-link text-light-blue'
                        target='_blank'
                        rel='noopener noreferrer'
                        {...props}
                      >
                        {children}
                      </a>
                    ) : (
                      <span {...props}>{children}</span>
                    );
                  },
                  h1: ({ children, node, ...props }) => {
                    return (
                      <h1 className={`d-none title-h3-bold mb-4`} {...props}>
                        {children}
                      </h1>
                    );
                  },
                  h2: ({ children, node, ...props }) => {
                    return (
                      <h2 className='title-h5-bold mb-2 specific-top-margin-4' {...props}>
                        {children}
                      </h2>
                    );
                  },
                  h3: ({ children, node, ...props }) => {
                    return (
                      <h2 className='title-h5 mb-2' {...props}>
                        {children}
                      </h2>
                    );
                  },
                  ul: ({ children, node, ...props }) => {
                    return (
                      <ul className='specific-left-padding-17 mb-3' {...props}>
                        {children}
                      </ul>
                    );
                  },
                  p: ({ children, node, ...props }) => {
                    return (
                      <div {...props}>
                        <div className='d-flex justify-content-between gap-10 snapshot-component-li-content'>
                          <p className={`${noContentFound ? 'briefing-item-empty title-h5-m light-60-text mb-0' : ''}`}>
                            {children}
                          </p>
                          {}
                          {!notShowThumbsDown && !isMobile && (
                            <ThumbDown
                              item={item}
                              node={node}
                              updateState={updateState}
                              briefingState={briefingState}
                            />
                          )}
                        </div>
                      </div>
                    );
                  },
                }}
              >
                {body}
              </ReactMarkdown>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ThumbDown = (props) => {
  const { node, item, briefingState } = props;
  const { briefing, setBriefing } = briefingState ?? {};
  const [isLoading, setIsLoading] = useState(false);
  const { addFeedback, removeFeedback } = useSnapshotFunctions();

  const findAnchorElement = (node) => {
    if (node?.type === 'element' && node?.tagName === 'a') {
      return node;
    }
    if (node?.children) {
      for (const child of node.children) {
        const found = findAnchorElement(child);
        if (found) return found;
      }
    }
    return null;
  };

  const url = findAnchorElement(node)?.properties?.href;
  const alreadyMarked = item?.itemFeedback?.find((item) => item?.feedbackType === 'NotImportant' && item?.url === url);

  const handleFeedback = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setIsLoading(true);
      if (alreadyMarked) {
        await removeFeedback({ snapshotItemId: item?.id, url: url });
      } else {
        await addFeedback({ snapshotItemId: item?.id, url: url });
      }
      if (setBriefing && briefing) {
        const newFeedbackItem = {
          feedbackType: 'NotImportant',
          url: url,
        };
        const newItem = {
          ...item,
          itemFeedback: alreadyMarked ? null : [...(item?.itemFeedback || []), newFeedbackItem],
        };
        let newContent = [...(briefing?.content || [])];
        newContent = newContent.map((item) => {
          if (item?.id === newItem?.id) {
            return newItem;
          }
          return item;
        });
        setBriefing((prevBriefing) => ({
          ...prevBriefing,
          content: newContent,
        }));
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  return (
    <button
      className={`button-thumbs-down ${
        alreadyMarked ? 'button-thumbs-down-default' : ''
      } general-button circle-button flex-centered justify-content-center ml-1`}
      data-tooltip-content={
        alreadyMarked
          ? 'Remove feedback'
          : `Providing feedback will help us prioritise\n what to show you in the future`
      }
      data-tooltip-class-name='general-tooltip tooltip-width-280'
      data-tooltip-id='general-tooltip'
      onClick={handleFeedback}
      disabled={isLoading}
    >
      {isLoading ? (
        <i className='main-subtle-text fas fa-spinner fa-spin d-inline-block' />
      ) : (
        <span className={`paragraph-p1 d-inline-block icon-thumbs-down${alreadyMarked ? '-complete' : ''}`} />
      )}
    </button>
  );
};

const DashboardUpdateLinkItem = (props) => {
  const { restoreScrollPosition, resetEventParameter, updateState, href, children, ...restProps } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [isEmbargoed, setIsEmbargoed] = useState(false);
  const [timesOverItem, setTimesOverItem] = useState(0);
  const [userCanNavigate, setUserCanNavigate] = useState(false);
  const [hasFinishedCall, setHasFinishedCall] = useState(false);

  const isMobile = useIsMobile();
  const { setUpdatePanelState, updatePanelState } = updateState ?? {};
  const { getNewResult } = useLoadContentResults({});
  const navigate = useNavigate();

  const url = href.replace('https:/', '');
  const { path, propId, propType } = createURLProps({ url }) ?? {};

  const callContent = useRef(null);
  callContent.current = async () => {
    try {
      setIsLoading(true);
      const results = await getNewResult({
        nameSection: transformToPascalCase(path),
        path,
        propType,
        propId,
        returnCompleteRequest: true,
      });
      setUserCanNavigate(results?.status === 200);
      setHasFinishedCall(true);
      setIsEmbargoed(results?.data?.embargoed);
      setIsLoading(false);
    } catch (error) {}
  };

  const loadApiOnHover = useRef(null);

  loadApiOnHover.current = async (e) => {
    if (!isLoading && timesOverItem === 0) {
      setTimesOverItem(timesOverItem + 1);
      await callContent.current();
    }
  };
  return (
    <Link
      className={`general-link ${
        isLoading ? 'disabled-link' : isEmbargoed ? 'text-light-red hover-text-light-red' : 'text-light-blue'
      } ${userCanNavigate ? '' : 'cursor-auto'}`}
      onClick={(e) => {
        if (!isMobile && setUpdatePanelState && resetEventParameter) {
          preventLink(e);
          const timesClicked = updatePanelState?.timesClicked?.[url] ?? 0;
          const newNumber = timesClicked + 1;
          if (hasFinishedCall) {
            if (!userCanNavigate && url?.includes('/calendar')) {
              navigate(url);
            } else {
              setUpdatePanelState({
                open: true,
                url: userCanNavigate ? url : 'noContent',
                timesClicked: { ...timesClicked, [url]: newNumber },
                highlightUpdate: newNumber > 1,
              });
              if (restoreScrollPosition && newNumber <= 1) {
                const clickedElement = e?.target?.closest('a');
                const linkTop = clickedElement?.getBoundingClientRect()?.top ?? e?.clientY;
                restoreScrollPosition(linkTop, url, e?.target?.innerText);
              }
            }
          }
        }
      }}
      to={href}
      onMouseOver={(e) => {
        if (!isMobile) {
          loadApiOnHover.current(e);
        }
      }}
      {...restProps}
    >
      {children}
    </Link>
  );
};

export default BriefingItem;
